import React, { useState, useEffect, useMemo } from 'react';
import { Vehicule, Intervention } from '../../../../shared/types/game';
import { VehiculeConfig, FonctionArmement, Competence, PersonnelConfig } from '../../../../shared/types/config';
import { getApiBaseUrl } from '../../../../core/config/api';
import { getServiceIdFromBuildingType } from '../../utils/buildingServiceMapping';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../core/store/store';
import { updateUser } from '../../../../core/store/slices/userSlice';
import VehiculeAddModal from './VehiculeAddModal';
import VehiculeDetailsModal from './VehiculeDetailsModal';

interface BuildingVehiculesTabProps {
  vehicules: Vehicule[];
  loadingVehicules: boolean;
  buildingType: string;
  buildingId: number;
  userId: number;
  onVehiculeAdded?: () => void;
}

const BuildingVehiculesTab: React.FC<BuildingVehiculesTabProps> = ({ 
  vehicules, 
  loadingVehicules,
  buildingType,
  buildingId,
  userId,
  onVehiculeAdded
}) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const [showAddModal, setShowAddModal] = useState(false);
  const [vehiculeConfigs, setVehiculeConfigs] = useState<VehiculeConfig[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [showVehiculeDetailsModal, setShowVehiculeDetailsModal] = useState(false);
  const [selectedVehicule, setSelectedVehicule] = useState<Vehicule | null>(null);
  const [fonctionsArmement, setFonctionsArmement] = useState<FonctionArmement[]>([]);
  const [competences, setCompetences] = useState<Competence[]>([]);
  const [personnelConfigs, setPersonnelConfigs] = useState<PersonnelConfig[]>([]);
  const [vehiculeConfigDetails, setVehiculeConfigDetails] = useState<VehiculeConfig | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [vehiculeSearchQuery, setVehiculeSearchQuery] = useState('');
  const [vehiculeFilterStatut, setVehiculeFilterStatut] = useState<string>('all'); // 'all', 'disponible', 'en_mission', 'en_maintenance', 'panne'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [viewMode, setViewMode] = useState<'compact' | 'detailed'>('compact');
  const [interventions, setInterventions] = useState<Intervention[]>([]);

  // Obtenir l'ID du service correspondant au type de bâtiment
  const serviceId = getServiceIdFromBuildingType(buildingType);

  // Charger les interventions pour calculer le nombre d'interventions par véhicule
  useEffect(() => {
    const loadInterventions = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          return;
        }

        const response = await fetch(`${getApiBaseUrl(true)}/buildings/interventions`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.data)) {
            setInterventions(data.data);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des interventions:', error);
      }
    };

    loadInterventions();
  }, []);

  // Calculer le nombre d'interventions par véhicule
  const interventionsParVehicule = useMemo(() => {
    const countMap = new Map<number, number>();
    
    interventions.forEach((intervention) => {
      if (intervention.vehicules_ids && Array.isArray(intervention.vehicules_ids)) {
        intervention.vehicules_ids.forEach((vehiculeId) => {
          const currentCount = countMap.get(vehiculeId) || 0;
          countMap.set(vehiculeId, currentCount + 1);
        });
      }
    });
    
    return countMap;
  }, [interventions]);

  // Filtrer les véhicules avec useMemo pour optimiser les performances
  const filteredVehicules = useMemo(() => {
    return vehicules.filter((v) => {
      // Filtre par recherche (nom, type)
      const searchLower = vehiculeSearchQuery.toLowerCase().trim();
      if (searchLower && !(
        v.nom.toLowerCase().includes(searchLower) ||
        v.type.toLowerCase().includes(searchLower)
      )) {
        return false;
      }

      // Filtre par statut
      if (vehiculeFilterStatut !== 'all' && v.statut !== vehiculeFilterStatut) {
        return false;
      }

      return true;
    });
  }, [vehicules, vehiculeSearchQuery, vehiculeFilterStatut]);

  // Pagination
  const totalPages = Math.ceil(filteredVehicules.length / itemsPerPage);
  const paginatedVehicules = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredVehicules.slice(startIndex, endIndex);
  }, [filteredVehicules, currentPage, itemsPerPage]);

  // Réinitialiser la page quand le filtre change
  useEffect(() => {
    setCurrentPage(1);
  }, [vehiculeSearchQuery, vehiculeFilterStatut]);

  // Charger les configurations de véhicules filtrées par service
  useEffect(() => {
    const loadVehiculeConfigs = async () => {
      if (!serviceId) return;

      setLoadingConfigs(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${getApiBaseUrl(true)}/config/vehicules`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.data)) {
            // Filtrer par service
            const filteredConfigs = data.data.filter((config: VehiculeConfig) => 
              config.id_service === serviceId
            );
            setVehiculeConfigs(filteredConfigs);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des configurations de véhicules:', error);
      } finally {
        setLoadingConfigs(false);
      }
    };

    // Charger les configurations au montage du composant pour afficher les capacités
    loadVehiculeConfigs();
  }, [serviceId]);

  // Fonction pour ouvrir la modal de détails
  const handleOpenVehiculeDetails = async (vehicule: Vehicule) => {
    setSelectedVehicule(vehicule);
    setShowVehiculeDetailsModal(true);
    setLoadingDetails(true);
    setVehiculeConfigDetails(null);
    setPersonnelConfigs([]);

    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingDetails(false);
      return;
    }

    try {
      const [vehiculesRes, fonctionsRes, competencesRes, personnelRes] = await Promise.all([
        fetch(`${getApiBaseUrl(true)}/config/vehicules`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${getApiBaseUrl(true)}/config/fonctions-armement`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${getApiBaseUrl(true)}/config/competences`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${getApiBaseUrl(true)}/config/personnel`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      // Trouver la configuration correspondant au véhicule (par nom ou type)
      if (vehiculesRes.ok) {
        const vehiculesData = await vehiculesRes.json();
        if (vehiculesData.success && Array.isArray(vehiculesData.data)) {
          const config = vehiculesData.data.find(
            (c: VehiculeConfig) =>
              vehicule.config_vehicle_id != null &&
              c.id != null &&
              c.id === vehicule.config_vehicle_id
          ) || vehiculesData.data.find((c: VehiculeConfig) =>
            c.nom === vehicule.nom || c.type === vehicule.type
          );
          if (config) {
            setVehiculeConfigDetails(config);
          }
        }
      }

      if (fonctionsRes.ok) {
        const fonctionsData = await fonctionsRes.json();
        if (fonctionsData.success && Array.isArray(fonctionsData.data)) {
          setFonctionsArmement(fonctionsData.data);
        }
      }

      if (competencesRes.ok) {
        const competencesData = await competencesRes.json();
        if (competencesData.success && Array.isArray(competencesData.data)) {
          setCompetences(competencesData.data);
        }
      }

      if (personnelRes.ok) {
        const personnelData = await personnelRes.json();
        if (personnelData.success && Array.isArray(personnelData.data)) {
          setPersonnelConfigs(personnelData.data);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSaveNom = async (nom: string) => {
    if (!selectedVehicule) {
      return;
    }

    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${getApiBaseUrl(true)}/buildings/vehicules/${selectedVehicule.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ nom })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Mettre à jour le véhicule dans la liste locale
        setSelectedVehicule({ ...selectedVehicule, nom });
        // Recharger la liste des véhicules
        if (onVehiculeAdded) {
          onVehiculeAdded();
        }
      } else {
        alert(data.error || 'Erreur lors de la sauvegarde du nom');
        throw new Error(data.error || 'Erreur lors de la sauvegarde du nom');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du nom:', error);
      alert('Erreur lors de la sauvegarde du nom');
      throw error;
    }
  };

  const getVehiculeConfig = (vehicule: Vehicule): VehiculeConfig | null => {
    return (
      vehiculeConfigs.find(
        (config) =>
          vehicule.config_vehicle_id != null &&
          config.id != null &&
          config.id === vehicule.config_vehicle_id
      ) ||
      vehiculeConfigs.find(
        (config) => config.nom === vehicule.nom || config.type === vehicule.type
      ) ||
      null
    );
  };

  // Composant mémorisé pour chaque carte de véhicule
  const VehiculeCard = React.memo<{ v: Vehicule; compact: boolean; onOpenDetails: (v: Vehicule) => void; nbInterventions: number; config: VehiculeConfig | null }>(({ v, compact, onOpenDetails, nbInterventions, config }) => {
    const getStatutColors = (statut: string) => {
      switch (statut) {
        case 'disponible':
          return 'bg-green-500/20 text-green-400';
        case 'en_mission':
          return 'bg-yellow-500/20 text-yellow-400';
        case 'en_maintenance':
          return 'bg-orange-500/20 text-orange-400';
        case 'panne':
          return 'bg-red-500/20 text-red-400';
        default:
          return 'bg-gray-500/20 text-gray-400';
      }
    };

    const getStatutLabel = (statut: string) => {
      switch (statut) {
        case 'disponible':
          return 'Disponible';
        case 'en_mission':
          return 'En mission';
        case 'en_maintenance':
          return 'En maintenance';
        case 'panne':
          return 'Panne';
        default:
          return statut;
      }
    };

    if (compact) {
      // Vue compacte en format carré/bloc
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 hover:border-blue-500 transition-colors aspect-square flex flex-col items-center justify-center relative group">
          {/* Badge statut en haut à droite */}
          <div className="absolute top-2 right-2">
            <span className={`px-1.5 py-0.5 rounded-full text-xs ${getStatutColors(v.statut)}`}>
              {v.statut === 'disponible' ? '✓' : v.statut === 'en_mission' ? '→' : v.statut === 'en_maintenance' ? '⚙' : '✗'}
            </span>
          </div>

          {/* Nom du véhicule */}
          <h4 className="text-sm font-semibold text-white text-center truncate w-full px-2 mb-1">
            {v.nom}
          </h4>

          {/* Type */}
          <span className="text-xs text-blue-400 text-center mb-1">
            {v.type}
          </span>

          {/* Nombre d'interventions */}
          <div className="mb-2">
            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
              {nbInterventions} intervention{nbInterventions > 1 ? 's' : ''}
            </span>
          </div>

          {/* Kilométrage */}
          <span className="text-xs text-gray-400 text-center mb-2">
            {v.kilometrage.toLocaleString()} km
          </span>

          {/* Capacités de transport */}
          {config && (
            <div className="mt-auto pt-2 border-t border-gray-700 w-full space-y-1">
              {(config.capable_escorte_medicale || config.capable_escorte_fdo) && (
                <div className="text-center">
                  <span className="text-xs text-gray-400">
                    {config.capable_escorte_medicale && 'Esc. Méd. '}
                    {config.capable_escorte_fdo && 'Esc. FDO'}
                  </span>
                </div>
              )}
              {(config.capacite_victimes_ur || config.capacite_victimes_ua || config.capacite_victimes_impliquees || config.capacite_hostiles || config.capacite_vehicules_accidentes) && (
                <div className="text-center text-xs text-gray-400">
                  {config.capacite_victimes_ur && `UR: ${config.capacite_victimes_ur} `}
                  {config.capacite_victimes_ua && `UA: ${config.capacite_victimes_ua} `}
                  {config.capacite_victimes_impliquees && `Impl: ${config.capacite_victimes_impliquees} `}
                  {config.capacite_hostiles && `Host: ${config.capacite_hostiles} `}
                  {config.capacite_vehicules_accidentes && `Véh: ${config.capacite_vehicules_accidentes}`}
                </div>
              )}
            </div>
          )}

          {/* Menu options (affiché au hover) */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetails(v);
            }}
            className="absolute top-2 left-2 text-gray-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
            title="Voir les détails"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        </div>
      );
    }

    // Vue détaillée
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-blue-500 transition-colors">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-white">{v.nom}</h4>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                {v.type}
              </span>
              <span className={`px-2 py-1 rounded text-xs ${getStatutColors(v.statut)}`}>
                {getStatutLabel(v.statut)}
              </span>
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                {nbInterventions} intervention{nbInterventions > 1 ? 's' : ''}
              </span>
              {v.capacite && (
                <span className="text-xs text-gray-400">
                  Capacité: {v.capacite}
                </span>
              )}
              <span className="text-xs text-gray-400">
                {v.kilometrage.toLocaleString()} km
              </span>
            </div>
            {/* Capacités de transport */}
            {config && (
              <div className="mt-3 space-y-2">
                {/* Escortes */}
                {(config.capable_escorte_medicale || config.capable_escorte_fdo) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {config.capable_escorte_medicale && (
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                        Escorte Médicale
                      </span>
                    )}
                    {config.capable_escorte_fdo && (
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">
                        Escorte FDO
                      </span>
                    )}
                  </div>
                )}
                {/* Capacités de transport */}
                {(config.capacite_victimes_ur || config.capacite_victimes_ua || config.capacite_victimes_impliquees || config.capacite_hostiles || config.capacite_vehicules_accidentes) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {config.capacite_victimes_ur !== undefined && config.capacite_victimes_ur > 0 && (
                      <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                        UR: {config.capacite_victimes_ur}
                      </span>
                    )}
                    {config.capacite_victimes_ua !== undefined && config.capacite_victimes_ua > 0 && (
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">
                        UA: {config.capacite_victimes_ua}
                      </span>
                    )}
                    {config.capacite_victimes_impliquees !== undefined && config.capacite_victimes_impliquees > 0 && (
                      <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-xs">
                        Impliqués: {config.capacite_victimes_impliquees}
                      </span>
                    )}
                    {config.capacite_hostiles !== undefined && config.capacite_hostiles > 0 && (
                      <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
                        Hostiles: {config.capacite_hostiles}
                      </span>
                    )}
                    {config.capacite_vehicules_accidentes !== undefined && config.capacite_vehicules_accidentes > 0 && (
                      <span className="px-2 py-1 bg-gray-500/20 text-gray-400 rounded text-xs">
                        Véhicules: {config.capacite_vehicules_accidentes}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetails(v);
            }}
            className="ml-4 text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
            title="Voir les détails"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        </div>
      </div>
    );
  });

  VehiculeCard.displayName = 'VehiculeCard';

  // Fonction pour créer un véhicule
  const handleCreateVehicule = async (config: VehiculeConfig) => {
    if (!currentUser || isCreating) return;

    setIsCreating(true);
    setCreateError(null);
    setCreateSuccess(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setCreateError('Non authentifié');
        return;
      }

      const response = await fetch(`${getApiBaseUrl(true)}/buildings/vehicules/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          configId: config.id,
          batimentId: buildingId
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setCreateError(data.error || 'Erreur lors de la création du véhicule');
        return;
      }

      // Mettre à jour l'argent de l'utilisateur
      if (data.data && data.data.newMoney !== undefined && currentUser) {
        dispatch(updateUser({ argents: data.data.newMoney }));
      }

      setCreateSuccess(data.message || 'Véhicule créé avec succès');
      
      // Appeler le callback pour rafraîchir les données
      if (onVehiculeAdded) {
        setTimeout(() => {
          onVehiculeAdded();
          setShowAddModal(false);
          setCreateSuccess(null);
        }, 1000);
      } else {
        setTimeout(() => {
          setShowAddModal(false);
          setCreateSuccess(null);
        }, 1000);
      }
    } catch (error: any) {
      console.error('Erreur lors de la création du véhicule:', error);
      setCreateError('Erreur réseau lors de la création du véhicule');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">
          Véhicules ({filteredVehicules.length}{filteredVehicules.length !== vehicules.length ? ` / ${vehicules.length}` : ''})
        </h3>
        <button 
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          + Ajouter
        </button>
      </div>

      {loadingVehicules ? (
        <div className="text-center py-12 text-gray-400">
          Chargement des véhicules...
        </div>
      ) : vehicules.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg mb-2">Aucun véhicule assigné</p>
          <p className="text-sm">Cliquez sur "Ajouter" pour assigner un véhicule à ce bâtiment</p>
        </div>
      ) : (
        <>
          {/* Barre de recherche et filtres */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Rechercher par nom ou type..."
                  value={vehiculeSearchQuery}
                  onChange={(e) => setVehiculeSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 pl-10 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <svg
                  className="absolute left-3 top-2.5 w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <select
                value={vehiculeFilterStatut}
                onChange={(e) => setVehiculeFilterStatut(e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tous les statuts</option>
                <option value="disponible">Disponible</option>
                <option value="en_mission">En mission</option>
                <option value="en_maintenance">En maintenance</option>
                <option value="panne">Panne</option>
              </select>
              <div
                className="flex shrink-0 overflow-hidden rounded-lg border border-gray-600"
                role="group"
                aria-label="Mode d’affichage liste véhicules"
              >
                <button
                  type="button"
                  onClick={() => setViewMode('compact')}
                  title="Vue compacte"
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium ${
                    viewMode === 'compact'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z" />
                  </svg>
                  <span className="hidden sm:inline">Compacte</span>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('detailed')}
                  title="Vue détaillée"
                  className={`flex items-center gap-2 border-l border-gray-600 px-3 py-2 text-sm font-medium ${
                    viewMode === 'detailed'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z" />
                  </svg>
                  <span className="hidden sm:inline">Détaillée</span>
                </button>
              </div>
            </div>
          </div>

          {/* Liste des véhicules avec hauteur fixe et scroll */}
          <div className="max-h-[500px] overflow-y-auto pr-2">
            {filteredVehicules.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p className="text-lg mb-2">Aucun résultat trouvé</p>
                <p className="text-sm">Essayez de modifier vos filtres de recherche</p>
              </div>
            ) : (
              <div className={`grid ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3' : 'grid-cols-1 gap-3'}`}>
                {paginatedVehicules.map((v) => (
                  <VehiculeCard 
                    key={v.id} 
                    v={v} 
                    compact={viewMode === 'compact'} 
                    onOpenDetails={handleOpenVehiculeDetails}
                    nbInterventions={interventionsParVehicule.get(v.id) || 0}
                    config={getVehiculeConfig(v)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2 border-t border-gray-700">
              <div className="text-sm text-gray-400">
                Page {currentPage} sur {totalPages} • {filteredVehicules.length} résultat{filteredVehicules.length > 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed text-white rounded text-sm transition-colors"
                >
                  Précédent
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded text-sm ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 hover:bg-gray-600 text-white'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed text-white rounded text-sm transition-colors"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal d'ajout de véhicule */}
      <VehiculeAddModal
        show={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSearchQuery('');
        }}
        vehiculeConfigs={vehiculeConfigs}
        loadingConfigs={loadingConfigs}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        isCreating={isCreating}
        onCreateVehicule={handleCreateVehicule}
        createError={createError}
        createSuccess={createSuccess}
      />

      {/* Modal de détails du véhicule */}
      <VehiculeDetailsModal
        show={showVehiculeDetailsModal}
        vehicule={selectedVehicule}
        vehiculeConfigDetails={vehiculeConfigDetails}
        fonctionsArmement={fonctionsArmement}
        competences={competences}
        personnelGradeConfigs={personnelConfigs}
        loadingDetails={loadingDetails}
        onClose={() => {
          setShowVehiculeDetailsModal(false);
          setSelectedVehicule(null);
        }}
        onSaveNom={handleSaveNom}
      />
    </div>
  );
};

export default BuildingVehiculesTab;
